import { faArrowRightArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelL } from '@sorare/core/src/atoms/typography';
import { ExtraMessageWithBlurredBg } from '@sorare/core/src/components/onboarding/ExtraMessageWithBlurredBg';
import { HighlightOverlay } from '@sorare/core/src/components/onboarding/HighlightOverlay';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { NBA_S3_PLAY_COMMON_MARKET } from '@sorare/core/src/constants/__generated__/routes';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import useLifecycle from '@sorare/core/src/hooks/useLifecycle';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { scrollIntoViewAndWait } from '@sorare/core/src/lib/scrollIntoViewAndWait';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CommonMarketStatus } from '@sorare/marketplace/src/components/commonCard/CommonMarketStatus';

const Root = styled(Horizontal).attrs({ gap: 2 })`
  z-index: 1;
  width: 100%;
  background-color: var(--c-neutral-300);
  justify-content: space-between;
  border-top: 1px solid var(--c-neutral-400);
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    justify-content: center;
  }
`;

export const MyCardsCommonMarket = () => {
  const navigate = useNavigate();
  const { lifecycle } = useLifecycle();
  const isTabletAndAbove = useIsTabletAndAbove();
  const {
    flags: { useRailsToPowerNba = false },
  } = useFeatureFlags();
  const rootRef = useRef<HTMLDivElement>(null);
  const [showOnboarding, setShowOnboarding] = useState(isTabletAndAbove);

  useEffect(() => {
    if (!lifecycle?.sawCommonMarketOnboarding && rootRef.current) {
      scrollIntoViewAndWait(rootRef.current).then(() => {
        setShowOnboarding(true);
      });
    }
  }, [lifecycle?.sawCommonMarketOnboarding]);

  if (!useRailsToPowerNba) return null;
  return (
    <>
      <OnboardingHighlightWrapper
        highlightName="MyCardsCommonMarket"
        wrap={!lifecycle?.sawCommonMarketOnboarding}
      >
        <Root ref={rootRef}>
          <Vertical>
            <BodyM bold>
              <FormattedMessage
                id="myCards.commonMarket.title"
                defaultMessage="Common Market"
              />
            </BodyM>
            <CommonMarketStatus />
          </Vertical>
          <Button
            size="medium"
            color="secondary"
            to={NBA_S3_PLAY_COMMON_MARKET}
            startIcon={<FontAwesomeIcon icon={faArrowRightArrowLeft} />}
          >
            <FormattedMessage {...fantasy.swap} />
          </Button>
        </Root>
      </OnboardingHighlightWrapper>
      {!lifecycle?.sawCommonMarketOnboarding && showOnboarding && (
        <HighlightOverlay
          stayOpen
          extras={[
            {
              nextTo: 'MyCardsCommonMarket',
              render: props => (
                <ExtraMessageWithBlurredBg
                  center
                  anchor="top"
                  positionProps={props}
                  fullWidth={!isTabletAndAbove}
                >
                  <LabelL>
                    <FormattedMessage
                      id="CommonMarket.onboarding.title"
                      defaultMessage="Common Cards are free! You can swap yours here when you’re ready"
                    />
                  </LabelL>
                </ExtraMessageWithBlurredBg>
              ),
            },
          ]}
          highlightNames={['MyCardsCommonMarket']}
          onClose={() => navigate(NBA_S3_PLAY_COMMON_MARKET)}
        />
      )}
    </>
  );
};
